import React, { useEffect, useState } from 'react';
import { Stack, IStackStyles, IStackTokens } from '@fluentui/react';

// Interfaz para la respuesta del endpoint
interface EnvironmentResponse {
  databaseName: string;
  environment: string;
}

// Interfaz para los errores de la API
interface ApiError {
  error: string;
  details?: string;
}

// Componente de barra de entorno
const EnvironmentBar: React.FC = () => {
  const [isTestEnv, setIsTestEnv] = useState<boolean>(false);
  const [isDevEnv, setIsDevEnv] = useState<boolean>(false);
  const [isUnknownEnv, setIsUnknownEnv] = useState<boolean>(false);
  const [environmentName, setEnvironmentName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const fetchEnvironmentInfo = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/environment`,
        );

        if (!response.ok) {
          const errorData: ApiError = await response.json();
          throw errorData;
        }

        const data: EnvironmentResponse = await response.json();

        // Determinar el entorno basado en la respuesta
        const envName = data.environment.toLowerCase();

        setIsTestEnv(envName.includes('test'));
        setIsDevEnv(envName.includes('dev'));
        setIsUnknownEnv(envName.includes('unknown'));
        setEnvironmentName(data.environment);
      } catch (err) {
        console.error('Error fetching environment info:', err);
        if ((err as ApiError).error) {
          setError(err as ApiError);
        } else {
          setError({ error: 'Error al cargar la información del entorno' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEnvironmentInfo();
  }, []);

  // Definimos el color de fondo basado en el entorno
  const getBackgroundColor = () => {
    if (isTestEnv) return 'red';
    if (isDevEnv) return 'blue';
    if (isUnknownEnv) return 'yellow';
    if (error) return 'red';
  };

  // Definimos los estilos para el Stack
  const stackStyles: IStackStyles = {
    root: {
      backgroundColor: getBackgroundColor(),
      color: isUnknownEnv ? 'black' : 'white',
      padding: '8px',
      width: '100%',
      height: '30px',
    },
  };

  const stackTokens: IStackTokens = {
    childrenGap: 10,
  };

  if (loading) {
    return null;
  }

  // Si hay un error, mostramos el Stack con el mensaje de error
  if (error) {
    return (
      <Stack
        horizontal
        horizontalAlign="center"
        verticalAlign="center"
        styles={stackStyles}
        tokens={stackTokens}
      >
        <Stack>
          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {error.error}
          </span>
        </Stack>
      </Stack>
    );
  }

  if (!isTestEnv && !isDevEnv && !isUnknownEnv) {
    return null;
  }

  return (
    <Stack
      horizontal
      horizontalAlign="center"
      verticalAlign="center"
      styles={stackStyles}
      tokens={stackTokens}
    >
      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
        {`Entorno de ${environmentName}`}
      </span>
    </Stack>
  );
};

export default EnvironmentBar;
